import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useHasPermission } from "../../common/Hooks";
import PubSub from "../../services/PubSub";
import User from "../../services/user";
import Utils from "../../utils/utils";
import List from "../controls/List";
import Toolbar from "../controls/Toolbar";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

const UserList = ({ basePath = '/user', onClickNew, onClick = () => { } }) => {
    const [translate] = useTranslation();
    const history = useHistory();
    const [userParams, setUserParams] = useState({});
    const [cursors, setCursors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [data, setData] = useState([]);
    const [domain, setDomain] = useState(Utils.getDomain());
    const [selectedUser, setSelectedUser] = useState({});
    const permissions = useHasPermission({
        domain: domain,
        environment: null,
        deployment: null,
        type: "user"
    });

    const getUsers = useCallback(async (params) => {
        const response = Utils.isRootDomain() ? await User.getAllUsers(
            params.firstCursor,
            params.lastCursor,
            params.pageIncrement < 0,
            params.getLatest
        )
            : await User.getAllUsersInDomain(
                params.domain,
                params.firstCursor,
                params.lastCursor,
                params.pageIncrement < 0,
                params.getLatest
            );

        if (response && !response.error) {
            setData(response.data.map((user) => ({ ...user, listLabel: `${user.email}${!user.isVerified ? ` [${translate('OTM_USER_NOT_VERIFIED')}]` : ''}` })));
            setCurrentPage(page => {
                const result = params.getLatest ? Math.ceil(response.paging.count / response.paging.pageSize) : params.userDeleted ? 1 : page + params.pageIncrement;
                setHasNextPage(response.paging.count > result * response.paging.pageSize);
                return result;
            });
            setCursors({
                firstCursor: response.paging.firstCursor,
                lastCursor: response.paging.lastCursor
            });

            const focusId = params.newUserId || response.data[0]?._id;

            if (focusId) {
                setSelectedUser(focusId);
                if (history.location?.pathname.startsWith(basePath)) {
                    history.push(`${basePath}/${focusId}`);
                }
            }
        }
    }, [history, basePath, translate]);

    useEffect(() => {
        if (userParams.domain) {
            getUsers(userParams);
        }
    }, [userParams, getUsers]);

    useEffect(() => {
        if (domain) {
            setUserParams({
                domain,
                pageIncrement: 0
            });
        }
        return () => setData([]);
    }, [domain]);

    useEffect(() => {
        const subscribeId = PubSub.subscribe("refresh-user-list", (params) => {
            setUserParams({
                domain,
                pageIncrement: 0,
                firstCursor: params?.userDeleted ? "" : cursors.firstCursor,
                lastCursor: params?.userDeleted ? "" : cursors.lastCursor,
                newUserId: params?.newUserId,
                userDeleted: params?.userDeleted,
                getLatest: !!params?.newUserId
            });
        });

        return () => {
            PubSub.unsubscribe("refresh-user-list", subscribeId);
        };
    }, [domain, cursors.firstCursor, cursors.lastCursor]);

    useEffect(() => {
        const subscribeId = PubSub.subscribe("domain", (domain) => {
            setDomain(domain);
        });

        return () => {
            PubSub.unsubscribe("domain", subscribeId);
        };
    }, []);

    const previous = useCallback(() => {
        setUserParams({
            domain,
            pageIncrement: -1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [domain, cursors.firstCursor, cursors.lastCursor]);

    const next = useCallback(() => {
        setUserParams({
            domain,
            pageIncrement: 1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [domain, cursors.firstCursor, cursors.lastCursor]);

    const handleClick = useCallback((_id) => {
        setSelectedUser(_id);
        history.push(`${basePath}/${_id}`);
        onClick();
    }, [basePath, history, onClick]);

    const renderToolbar = useMemo(() => {
        const buttons = [
            {
                name: "ChevronLeft",
                title: translate("OTM_PREVIOUS"),
                onClick: previous,
                disabled: currentPage === 1
            },
            {
                name: "ChevronRight",
                title: translate("OTM_NEXT"),
                onClick: next,
                disabled: !hasNextPage
            }
        ];

        if (permissions.create) {
            buttons.push({
                name: "Add",
                title: translate("OTM_NEW"),
                onClick: onClickNew,
                disabled: !permissions.create,
            });
        }

        return <Toolbar
            buttons={buttons}
            heading={translate("OTM_USERS")}
        />
    }, [translate, previous, currentPage, next, hasNextPage, onClickNew, permissions]);

    return (
        <div className="card">
            <div className="card-header border-bottom">
                {renderToolbar}
            </div>
            <div className="card-body">
                <List
                    onClick={handleClick}
                    attr="listLabel"
                    data={data}
                    selectedElement={selectedUser}
                    url={"user"}
                    type="user" />
            </div>
        </div>
    );
};

export default UserList;
